import React, { HTMLAttributes } from 'react'
import { IPHIComponent, getStyleForPHI } from '../phi/phi.component'
import { IonItem, IonLabel, IonRow, IonTextarea, useIonViewDidLeave } from '@ionic/react'
import Validation from '../validation/Validation.component'
import ValidationBadgeComponent from '../validationBadge/ValidationBadge.component'
import textAreaStyles from './TextArea.component.module.css'
import {
  resetPNNVState,
  setFreeTextFieldCurrentPNNVValue,
  setFreeTextFieldOnPNNVChangeHandler,
  setFreeTextFieldPNNVIsOpen,
  setFreeTextFieldPNNVLabel,
  setFreeTextFieldPNNVListHeader,
  setFreeTextFieldPNNVOptions,
  setFreeTextFieldNonPNNVDataReset,
  setFreeTextFieldPNNVShowConfirmation,
} from '../pnnv/store/pnnv.actions'
import { useDispatch, useSelector } from 'react-redux'
import { IOptions, IPNNVOption } from '../../common/common.types'
import { isPlatformWeb } from '../../utilities'
import { getPNNVStateSelector } from '../pnnv/selectors'
import PNNVIcon from '../pnnv/PNNVIcon.component'

export interface ITextArea extends HTMLAttributes<HTMLIonTextareaElement>, IPHIComponent {
  dataTestID: string
  label?: string
  value: string
  maxlength?: number
  rows?: number
  cols?: number
  validationId?: string
  disabled?: boolean
  onChangeAction: (e: CustomEvent) => void
  pnnvOptions?: IOptions
  pnnvValue?: IPNNVOption | null | undefined
  pnnvAction?: (...args: any[]) => any
  pnnvLabel?: string
  pnnvListHeader?: string
  action?: string
  pnnvShowConfirmation?: boolean
  className?: string
}

const TextArea: React.FC<ITextArea> = (props: ITextArea) => {
  const {
    dataTestID,
    value,
    rows,
    cols,
    maxlength = 250,
    onChangeAction,
    label,
    isPHI,
    validationId = '',
    pnnvOptions = [],
    pnnvValue = undefined,
    pnnvAction = () => void 0,
    pnnvLabel = '',
    pnnvListHeader = '',
    disabled = false,
    action,
    pnnvShowConfirmation = false,
    className = '',
  } = props

  const shouldShowPNNVPWAControl =
    isPlatformWeb() &&
    typeof pnnvOptions !== 'undefined' &&
    typeof pnnvValue !== 'undefined' &&
    typeof pnnvAction !== 'undefined'

  const dispatch = useDispatch()
  const { freeTextField } = useSelector(getPNNVStateSelector)

  const setPNNVData = () => {
    dispatch(setFreeTextFieldPNNVOptions(pnnvOptions))
    dispatch(setFreeTextFieldCurrentPNNVValue(pnnvValue))
    dispatch(setFreeTextFieldOnPNNVChangeHandler(pnnvAction))
    dispatch(setFreeTextFieldPNNVLabel(pnnvLabel))
    dispatch(setFreeTextFieldPNNVListHeader(pnnvListHeader))
    dispatch(setFreeTextFieldPNNVShowConfirmation(pnnvShowConfirmation))
  }

  useIonViewDidLeave(() => dispatch(resetPNNVState()))

  const handlePNNVOptions = (
    e: CustomEvent | React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLIonButtonElement>
  ) => {
    if (pnnvOptions.length) {
      const newEventObject = {
        ...e,
        detail: {
          ...e.detail,
          value: '',
        },
      }
      dispatch(setFreeTextFieldNonPNNVDataReset(() => onChangeAction(newEventObject as any)))
      setPNNVData()
      if (pnnvValue?.name) {
        dispatch(setFreeTextFieldPNNVIsOpen(true))
      }
    } else {
      dispatch(resetPNNVState())
    }
  }

  const handleOnChange = (e: CustomEvent) => {
    if (e.detail.value === value) return
    if (pnnvAction && pnnvValue) pnnvAction(null)
    onChangeAction(e)
    const hasValue = Boolean(e.detail.value)

    if (hasValue !== freeTextField.showConfirmation) {
      dispatch(setFreeTextFieldPNNVShowConfirmation(hasValue))
    }
  }

  return (
    <IonItem key={dataTestID} lines='none' className={className}>
      {action && <ValidationBadgeComponent action={action} />}
      <IonRow>
        {label && (
          <IonLabel
            className={
              shouldShowPNNVPWAControl ? textAreaStyles['text-area-label-pwa-pnnv'] : textAreaStyles['text-area-label']
            }
            color='medium'
            position='stacked'>
            {label}
          </IonLabel>
        )}
        {shouldShowPNNVPWAControl && pnnvValue == null && (
          <PNNVIcon
            pnnvOptions={pnnvOptions}
            pnnvLabel={pnnvLabel}
            pnnvValue={pnnvValue}
            pnnvListHeader={pnnvListHeader}
            pnnvAction={pnnvAction}
            onButtonClick={handlePNNVOptions}
            className='pnnv-icon-pwa-text-area'
            dataTestId={dataTestID + '-pnnvIcon'}
          />
        )}
      </IonRow>
      <IonRow className='w-100'>
        <div data-private={isPHI} style={getStyleForPHI(isPHI)}>
          {pnnvValue?.name ? (
            <div onClick={handlePNNVOptions}>
              <IonTextarea
                data-testid={dataTestID + '-pnnv'}
                rows={rows || 3}
                cols={cols || 20}
                value={pnnvValue.name}
                color='primary-alt'
                readonly={true}
                maxlength={maxlength}
                autocapitalize='sentences'
                disabled={disabled}
              />
            </div>
          ) : (
            <IonTextarea
              data-testid={dataTestID}
              rows={rows || 3}
              cols={cols || 20}
              value={value}
              color='primary-alt'
              onIonChange={(e) => {
                handleOnChange(e)
              }}
              onIonFocus={handlePNNVOptions}
              maxlength={maxlength}
              autocapitalize='sentences'
              disabled={disabled}
            />
          )}
          {maxlength && (
            <div className='footer'>
              <span>{value?.length >= maxlength && <Validation id={validationId} />}</span>
              <h1 className={value?.length < maxlength ? 'text-color-blue-tint' : 'text-color-red'}>
                {value?.length}/{maxlength}
              </h1>
            </div>
          )}
        </div>
      </IonRow>
    </IonItem>
  )
}

export default TextArea
